// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useMemo, useCallback } from 'react';

import type { CustomStatusesHookValue, EntityState } from 'components/Mantine/StatusEditor/types';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

type TasksContextType = CustomStatusesHookValue<'Task'>;

const TaskStatusContext = React.createContext<TasksContextType | undefined>(undefined);

export const TaskStatusProvider = ({ children }: { children: ReactNode }) => {
  const { data: taskStatusTypes, loading } = useEvolveGetWithLocalStorage('shop/task/statusType');

  const states = useMemo<EntityState<'Task'>[] | undefined>(
    () =>
      taskStatusTypes?.map((t) => ({
        stateId: t.taskStatusTypeId,
        name: t.taskStatusTypeName,
        statuses: [],
      })),
    [taskStatusTypes],
  );

  // Tasks do not yet support custom statuses
  const notImplemented = useCallback(async () => {
    throw new Error('Task custom statuses are not supported.');
  }, []);

  const value = useMemo<TasksContextType>(
    () => ({
      states,
      statuses: [],
      statusMap: {},
      loading,
      addStatus: notImplemented,
      updateStatus: notImplemented,
      deleteStatus: notImplemented,
      mutationRunning: false,
    }),
    [loading, notImplemented, states],
  );
  return <TaskStatusContext value={value}>{children}</TaskStatusContext>;
};

export const useTaskStatuses = () => useGeneralContext(TaskStatusContext, 'TaskStatus');
